import { type FC, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import KemChatComposerSection from "./components/KemChatComposerSection";
import KemChatInput from "./components/KemChatInput";
import KemChatPanel from "./components/KemChatPanel";
import KemChatPrivacyToolbar from "./components/KemChatPrivacyToolbar";
import KemHealthUnavailableBanner from "./components/KemHealthUnavailableBanner";
import KemLocationPermissionModal from "./components/KemLocationPermissionModal";
import KemPageShell from "./components/KemPageShell";
import KemTour from "./components/KemTour";
import KemUpgradeNoticeModal from "./components/KemUpgradeNoticeModal";
import MessageLimit from "./components/messageLimit";
import useKemChat from "./hooks/useKemChat";
import { useKemChatScroll } from "./hooks/useKemChatScroll";
import useKemLocation from "./hooks/useKemLocation";
import useLockBodyScroll from "./hooks/useLockBodyScroll";
import { useViewportUnderHeader } from "./hooks/useViewportUnderHeader";

const KemChatPage: FC = () => {
  const navigate = useNavigate();
  const { outerStyle } = useViewportUnderHeader();
  useLockBodyScroll();

  const {
    isLocationPermissionModalOpen,
    locationPermissionModalVariant,
    onCloseLocationPermissionModal,
    onConfirmLocationPermissionModal,
  } = useKemLocation();

  const {
    currentChat,
    handleSendMessage,
    handleSendVoice,
    handleGetNextRecentHistory,
    getIsLastPage,
    isLoadingHistory,
    isInitialHistoryLoading,
    isLoadingMoreHistory,
    loadingBotChat,
    kemIsTyping,
    kemSocketReachable,
    isConnecting,
    isTranscribing,
    setChatContainerRef,
    setMessageAudioUrl,
    handleMarkMessageVisible,
    onScrollToBottom,
    hasFetchedInitialData,
    isUpgradeNoticeModalOpen,
    onCloseUpgradeNoticeModal,
    blockSpeakToKem,
    canSpeakToKem,
    initiateKemUserSession,
    isLimitReached,
    setLatestMessageFollowScroll,
  } = useKemChat();

  const getCanPaginateAtTop = useCallback(
    () => hasFetchedInitialData.current && !getIsLastPage(),
    [getIsLastPage, hasFetchedInitialData],
  );

  const {
    combinedChatRef,
    onScroll,
    queueScrollToBottomAfterUpdate,
    scrollToBottom,
    showScrollToBottom,
    handleKemBubbleImageLoad,
  } = useKemChatScroll({
    currentChat,
    loadingBotChat,
    kemIsTyping,
    isLoadingHistory,
    isLoadingMoreHistory,
    handleGetNextRecentHistory,
    getCanPaginateAtTop,
    setChatContainerRef,
    fallbackScrollToBottom: onScrollToBottom,
  });

  useEffect(() => {
    setLatestMessageFollowScroll(queueScrollToBottomAfterUpdate);
    return () => setLatestMessageFollowScroll(null);
  }, [queueScrollToBottomAfterUpdate, setLatestMessageFollowScroll]);

  useEffect(() => {
    void initiateKemUserSession();
  }, [initiateKemUserSession]);

  const handleSubscribe = useCallback(() => {
    onCloseUpgradeNoticeModal();
    void navigate("/subscriptions");
  }, [navigate, onCloseUpgradeNoticeModal]);

  const handleTalkToKem = useCallback(() => {
    if (!canSpeakToKem) {
      blockSpeakToKem();
      return;
    }

    void navigate("/kem/speech");
  }, [blockSpeakToKem, canSpeakToKem, navigate]);

  const isCenterLoading = (isConnecting ?? false) || isInitialHistoryLoading;

  const afterCard = (
    <>
      <KemTour />
      <KemUpgradeNoticeModal
        isOpen={isUpgradeNoticeModalOpen}
        onClose={onCloseUpgradeNoticeModal}
        onSubscribe={handleSubscribe}
      />
      <KemLocationPermissionModal
        isOpen={isLocationPermissionModalOpen}
        onClose={onCloseLocationPermissionModal}
        onConfirm={onConfirmLocationPermissionModal}
        variant={locationPermissionModalVariant}
      />
    </>
  );

  return (
    <KemPageShell afterCard={afterCard} outerStyle={outerStyle}>
      <KemHealthUnavailableBanner visible={!kemSocketReachable} />

      <div className="relative flex min-h-0 flex-1 flex-col pt-2">
        <KemChatPrivacyToolbar
          canSpeakToKem={canSpeakToKem}
          onTalkToKem={handleTalkToKem}
        />

        <KemChatPanel
          combinedChatRef={combinedChatRef}
          handleMarkMessageVisible={handleMarkMessageVisible}
          isCenterLoading={isCenterLoading}
          isEmpty={currentChat.length === 0}
          messages={currentChat}
          onKemBubbleImageLoad={handleKemBubbleImageLoad}
          onScroll={onScroll}
          setMessageAudioUrl={setMessageAudioUrl}
          showTopPaginationLoader={!isLoadingHistory && isLoadingMoreHistory}
        />
      </div>

      <KemChatComposerSection
        inputArea={
          isLimitReached ? (
            <MessageLimit />
          ) : (
            <KemChatInput
              disabled={
                Boolean(loadingBotChat) || !kemSocketReachable || isTranscribing
              }
              onSend={(text) => {
                void handleSendMessage({ message: text });
              }}
              onSendVoice={(audioBlob: Blob) => void handleSendVoice(audioBlob)}
              placeholder="How may I help you?"
            />
          )
        }
        isTranscribing={isTranscribing}
        kemIsTyping={kemIsTyping}
        onScrollToNewer={() => scrollToBottom()}
        showScrollToBottom={showScrollToBottom}
      />
    </KemPageShell>
  );
};

export default KemChatPage;
